/**
 * "Is this the same problem?" (8 Oct) — the decisions around the check, kept
 * import-free so `tests/similar-tickets.test.mjs` can run them under Node.
 *
 * Just before an incident is filed, the reporter asks the server for open
 * tickets about the same shared system (EPLAN, 365FO …). It asks once per
 * attempt (the fingerprint of what the person entered): a retry of the same
 * attempt files straight away, with the same body, so the idempotency key
 * still replays. "No, mine is different" is remembered against that attempt
 * and adds the numbers to the body as `not_same_as`.
 */

/** The minimum of a match the decisions need. */
export interface SimilarMatchRef {
  ticket_number: string;
}

export interface SimilarCheckInput {
  /** Raised for a colleague: "add me" would add the reporter, so never asked. */
  forSomeoneElse: boolean;
  /** On a shared device the signed-in person is the device: never asked. */
  kiosk: boolean;
  /** The attempt already asked about, or null. */
  checkedFingerprint: string | null;
  fingerprint: string;
}

/** Whether to ask the server before filing this attempt. */
export function shouldCheckSimilar(input: SimilarCheckInput): boolean {
  return !input.forSomeoneElse && !input.kiosk && input.checkedFingerprint !== input.fingerprint;
}

/** The numbers the person said theirs is not, at most three, no blanks or repeats. */
export function notSameNumbers(matches: readonly SimilarMatchRef[]): string[] {
  const numbers: string[] = [];
  for (const match of matches) {
    const number = match.ticket_number.trim();
    if (number !== "" && !numbers.includes(number)) {
      numbers.push(number);
    }
  }
  return numbers.slice(0, 3);
}

/** A "No, mine is different" answer, for the attempt it was given in. */
export interface NotSameDecision {
  fingerprint: string;
  numbers: string[];
}

/**
 * The body to file: with `not_same_as` when this attempt was answered
 * "different", unchanged otherwise (so the body of any other attempt is what
 * it always was).
 */
export function withNotSameAs<T extends object>(
  body: T,
  fingerprint: string,
  decision: NotSameDecision | null,
): T & { not_same_as?: string[] } {
  if (decision === null || decision.fingerprint !== fingerprint || decision.numbers.length === 0) {
    return body;
  }
  return { ...body, not_same_as: decision.numbers };
}
